import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Alert,
  Button,
  buttonStyles,
  Card,
  EmptyState,
  HealthBadge,
  TableSkeleton,
  Mono,
  PageHeader,
  ServerStateBadge,
  Table,
  Td,
  Th,
  cx,
} from "../components/ui";
import { Pagination } from "../components/Pagination";
import { useCanWrite } from "../auth/AuthProvider";
import { SATURATED_ABOVE } from "../lib/capacity";
import { SAFE_ENGINE_VERSION, belowSafeVersion } from "../lib/engineVersion";
import { formatBytes, formatLatency, formatRelative } from "../lib/format";
import {
  useCheckServer,
  useRegionLookup,
  useRegions,
  useServers,
} from "../lib/queries";
import type { FleetSummary as FleetSummaryData, Server } from "../lib/types";

const LIMIT = 25;

type ListRow = { kind: "heading"; group: string } | { kind: "server"; server: Server };

/**
 * Sorts a page into groups and interleaves a heading wherever the group changes.
 *
 * The sort is done here rather than trusted from the API. The server orders by
 * region code, which *happens* to produce contiguous groups today only because
 * the codes share a prefix with their group — `ap-*` is Asia, `eu-*` is EU. Add
 * a `uk-south` to the EU group and alphabetical order drops it after `sa-east`,
 * splitting EU across two headings. Grouping by the group itself cannot drift.
 *
 * Headings carry no count: with pagination it could only ever be the count on
 * this page, which reads as the fleet total and is not.
 */
function withGroupHeadings(
  servers: Server[],
  groupOf: (region: string | null) => string,
  groupRank: (group: string) => number,
): ListRow[] {
  const ordered = [...servers].sort((a, b) => {
    const byGroup = groupRank(groupOf(a.region)) - groupRank(groupOf(b.region));
    if (byGroup !== 0) return byGroup;
    const byRegion = (a.region ?? "").localeCompare(b.region ?? "");
    return byRegion !== 0 ? byRegion : a.name.localeCompare(b.name);
  });

  const rows: ListRow[] = [];
  let current: string | null = null;
  for (const server of ordered) {
    const group = groupOf(server.region);
    if (group !== current) {
      rows.push({ kind: "heading", group });
      current = group;
    }
    rows.push({ kind: "server", server });
  }
  return rows;
}

/**
 * CPU and disk are kept together: they are the two host readings that explain
 * most placement and incident decisions without asking an operator to open
 * every server detail page. These are cached health-sweep samples, not a live
 * subscription, so their age stays visible.
 *
 * An em dash rather than an empty bar when nothing has been sampled: a bar at
 * zero reads as idle, which is a different and more dangerous claim than
 * "unknown" — and it is the claim that would make an unmonitored server look
 * like the emptiest one in the fleet.
 */
function LoadCell({ server }: { server: Server }) {
  const cpu = server.capacity.cpuPercent;
  const disk = percentage(server.capacity.diskUsedBytes, server.capacity.diskTotalBytes);
  const diskDetail =
    server.capacity.diskUsedBytes !== null && server.capacity.diskTotalBytes !== null
      ? `${formatBytes(server.capacity.diskUsedBytes)} / ${formatBytes(server.capacity.diskTotalBytes)}`
      : undefined;

  if (cpu === null && disk === null) {
    return (
      <div className="min-w-24">
        <span className="text-sm text-fg-subtle">—</span>
        <p className="text-xs text-fg-subtle">
          {server.monitoringConfigured ? "not sampled" : "no monitoring"}
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-32 space-y-2">
      <ResourceReading label="CPU" percent={cpu} />
      <ResourceReading label="Host disk" percent={disk} detail={diskDetail} />
      <p className="text-xs text-fg-subtle">{formatRelative(server.capacity.collectedAt)}</p>
    </div>
  );
}

function ResourceReading({
  label,
  percent,
  detail,
}: {
  label: string;
  percent: number | null;
  detail?: string;
}) {
  const rounded = percent === null ? null : Math.round(percent);
  const saturated = percent !== null && percent >= SATURATED_ABOVE * 100;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-fg-subtle">{label}</span>
        <span className={cx("tabular-nums", saturated && "text-danger")}>
          {rounded === null ? "—" : `${rounded}%`}
        </span>
      </div>
      {percent !== null && (
        <div className="mt-1 h-1 overflow-hidden rounded-full bg-line">
          <div
            className={cx(
              "h-full rounded-full transition-[width]",
              saturated ? "bg-danger" : percent >= 75 ? "bg-warn" : "bg-mint",
            )}
            style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
          />
        </div>
      )}
      {detail && <p className="mt-0.5 text-xs text-fg-subtle">{detail}</p>}
    </div>
  );
}

function percentage(used: number | null, total: number | null): number | null {
  if (used === null || total === null || total <= 0) return null;
  return (used / total) * 100;
}

/**
 * The fleet in four numbers, above the list.
 *
 * Fleet-wide rather than this page's, and the API counts them separately for
 * that reason. Hairline dividers instead of four separate cards: on a near-black
 * canvas the gaps between cards read as more structure than four related figures
 * deserve.
 *
 * Only two of these are ever alarming, so only those two can carry colour.
 */
function FleetSummary({ summary }: { summary: FleetSummaryData }) {
  const degraded = summary.servers - summary.healthy;

  return (
    <div className="grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2 lg:grid-cols-5">
      <SummaryTile label="Servers" value={summary.servers} />
      <SummaryTile
        label="Healthy"
        value={summary.healthy}
        tone={degraded > 0 ? "danger" : undefined}
        note={degraded > 0 ? `${degraded} degraded` : undefined}
      />
      <SummaryTile
        label="Organizations"
        value={summary.orgs}
        tone={summary.unlocated > 0 ? "warn" : undefined}
        note={summary.unlocated > 0 ? `${summary.unlocated} server(s) unplaced` : undefined}
      />
      <SummaryTile label="Apps" value={summary.apps} />
      {/*
        Whether the fleet is patched, which none of the tiles beside it can
        answer. `unsampled` is carried in the note rather than folded into the
        number: a server that has never reported an update state is not a
        server known to be current, and a single figure would say it was.
      */}
      <SummaryTile
        label="Updates"
        value={summary.updatable}
        tone={summary.updatable > 0 ? "warn" : undefined}
        note={
          summary.unsampled > 0
            ? `${summary.unsampled} not reported`
            : summary.updatable > 0
              ? "available"
              : undefined
        }
      />
    </div>
  );
}

function SummaryTile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: number;
  note?: string;
  tone?: "warn" | "danger";
}) {
  return (
    <div className="bg-surface px-5 py-4">
      <p className="text-xs uppercase tracking-wide text-fg-subtle">{label}</p>
      <p className="mt-1 text-2xl font-light tabular-nums tracking-tight text-fg">{value}</p>
      {/* The slot is held open whether or not there is a note, so the four
          tiles keep one baseline instead of stepping as counts change. */}
      <p
        className={cx(
          "mt-0.5 min-h-4 text-xs",
          tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : "text-fg-subtle",
        )}
      >
        {note ?? ""}
      </p>
    </div>
  );
}

export function ServersPage() {
  const [offset, setOffset] = useState(0);
  const { data, isPending, error } = useServers({ limit: LIMIT, offset });
  const canWrite = useCanWrite();
  const check = useCheckServer();
  const regionLookup = useRegionLookup();
  const { data: regions } = useRegions();

  // A server with no region still needs a heading to sit under, and "Unassigned"
  // is more honest than filing it under somebody else's group.
  const groupOf = useMemo(
    () => (region: string | null) => (region && regionLookup.get(region)?.group) || "Unassigned",
    [regionLookup],
  );

  // The catalogue arrives in the order the console should display, so its own
  // position is the ranking. Unknown groups — "Unassigned", or a region this
  // console is too old to know — sort last rather than first.
  const groupRank = useMemo(() => {
    const order = new Map((regions ?? []).map((entry, index) => [entry.group, index]));
    return (group: string) => order.get(group) ?? Number.MAX_SAFE_INTEGER;
  }, [regions]);

  return (
    <>
      <PageHeader
        title="Servers"
        description="The engines Maxlayer deploys onto. New tenants go to the nearest healthy one with room."
        action={
          canWrite && (
            <Link to="/servers/new" className={buttonStyles("primary", "md")}>
              Register server
            </Link>
          )
        }
      />

      {check.error && <Alert>{check.error.message}</Alert>}

      {!isPending && !error && data.data.length > 0 && <FleetSummary summary={data.summary} />}

      <Card>
        {isPending ? (
          <TableSkeleton columns={5} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            title="No servers registered"
            description="Tenants cannot create apps until one exists. Register an engine."
            action={
              canWrite ? (
                <Link to="/servers/new" className={buttonStyles("primary", "md")}>
                  Register server
                </Link>
              ) : undefined
            }
          />
        ) : (
          <>
          {/*
            Four columns rather than eight. The old shape put state, health,
            CPU, disk, orgs, apps and last-checked in six thin cells and hid half of
            them below a laptop, so the answer to "is this server alright" was
            spread across columns the operator could not see at once. Each
            column now carries its own supporting line instead.
          */}
          <Table
            head={
              <tr>
                <Th>Server</Th>
                <Th>Health</Th>
                <Th priority="sm">CPU / host disk</Th>
                <Th numeric priority="md">Tenants</Th>
                <Th />
              </tr>
            }
          >
            {withGroupHeadings(data.data, groupOf, groupRank).map((row) =>
              row.kind === "heading" ? (
                <tr key={`group-${row.group}`}>
                  <td
                    colSpan={5}
                    className="border-t border-line bg-raised/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle"
                  >
                    {row.group}
                  </td>
                </tr>
              ) : (
                <tr key={row.server.id} className="hover:bg-raised">
                  <Td>
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/servers/${row.server.id}`}
                        className="font-medium text-accent hover:underline"
                      >
                        {row.server.name}
                      </Link>
                      {row.server.state !== "active" && (
                        <ServerStateBadge state={row.server.state} />
                      )}
                    </div>
                    <p className="text-xs text-fg-subtle">
                      {row.server.region ? (
                        <>
                          {regionLookup.get(row.server.region)?.label ?? row.server.region}
                          <span className="mx-1.5 text-line-strong">·</span>
                        </>
                      ) : null}
                      {row.server.url}
                    </p>
                    {/*
                      Worth surfacing here rather than only in settings: an
                      unplaced server quietly stops receiving tenants as soon as
                      any other server has a position, and nothing else on this
                      page would say so.
                    */}
                    {!row.server.located && (
                      <p className="text-xs text-warn">No region — skipped by distance</p>
                    )}
                    {/*
                      The engine version, in the cell rather than a column of
                      its own: this table is four columns on purpose, and a
                      version is an attribute of the server rather than a
                      figure worth sorting the fleet by. The update marker is
                      the part an operator scans for.
                    */}
                    {row.server.version && (
                      <p className="text-xs text-fg-subtle">
                        <Mono>{row.server.version}</Mono>
                        {row.server.updateAvailable === true && (
                          <span className="ml-1.5 text-warn">
                            &rarr; <Mono>{row.server.latestVersion ?? "update"}</Mono>
                          </span>
                        )}
                        {belowSafeVersion(row.server.version) === true && (
                          <span
                            className="ml-1.5 text-danger"
                            title={`Before ${SAFE_ENGINE_VERSION} the engine ran customer input unquoted in a root shell. Update this server.`}
                          >
                            unsafe below {SAFE_ENGINE_VERSION}
                          </span>
                        )}
                      </p>
                    )}
                    {/*
                      A critical hardening finding is the one thing about a
                      server that must not wait for somebody to open it: a
                      container that has crossed the host boundary. The
                      summary rides the list row; the containers are on the
                      server's overview.
                    */}
                    {(row.server.hardening?.critical ?? 0) > 0 && (
                      <p
                        className="text-xs text-danger"
                        title="A running container is privileged, holds the Docker socket, shares a host namespace, or runs without seccomp or AppArmor. Open the server."
                      >
                        {row.server.hardening!.critical} critical container finding
                        {row.server.hardening!.critical === 1 ? "" : "s"}
                      </p>
                    )}
                  </Td>
                  <Td>
                    <HealthBadge health={row.server.health.status} />
                    <p className="mt-1 text-xs text-fg-subtle">
                      {formatRelative(row.server.health.checkedAt)}
                      {row.server.health.latencyMs !== null && (
                        <Mono className="ml-1.5">
                          {formatLatency(row.server.health.latencyMs)}
                        </Mono>
                      )}
                    </p>
                    {row.server.health.message && (
                      <p className="mt-1 max-w-xs truncate text-xs text-danger">
                        {row.server.health.message}
                      </p>
                    )}
                  </Td>
                  <Td priority="sm">
                    <LoadCell server={row.server} />
                  </Td>
                  <Td numeric priority="md">
                    <span className="text-sm">
                      {row.server.counts?.orgs ?? 0}
                      {row.server.maxOrgs !== null && (
                        <span className="text-fg-subtle"> / {row.server.maxOrgs}</span>
                      )}
                    </span>
                    <p className="text-xs text-fg-subtle">
                      {row.server.counts?.apps ?? 0} app
                      {row.server.counts?.apps === 1 ? "" : "s"}
                    </p>
                  </Td>
                  <Td className="text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={check.isPending && check.variables === row.server.id}
                      onClick={() => check.mutate(row.server.id)}
                    >
                      Check
                    </Button>
                  </Td>
                </tr>
              ),
            )}
          </Table>
          <Pagination
            total={data.total}
            limit={data.limit}
            offset={data.offset}
            onChange={setOffset}
          />
          </>
        )}
      </Card>

    </>
  );
}

